import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Loader from "Components/UI/Loader";
import { getLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import MaterialSelect from "@mui/material/Select";

import Label from "Components/UI/Label";
// Images
import images from "Assets/images";
// css
import "Components/Common/Modal/style.css";
import "Components/Core/BookingFormModule/new-booking-form.css";
import { useApiCall } from "Hooks/useApiCall";
import { MenuItem } from "@mui/material";
import {
  createVehicleEmission,
  updateVehicleEmission,
} from "Network/Core/VehicleMaster/vehicleEmission";
import { getFilteredCommonUtils } from "Network/Core/CommonUtils/commonUtils";
import {
  VehicleEmissionType,
  VehicleMasterType,
  VehicleType,
} from "Network/Core/VehicleMaster/types";
import React from "react";
import { getVehicleTypeList } from "Network/Core/Booking/booking";
import { useTranslationCheck } from "Hooks/useTranslationCheck";

export interface VehicleEmissionModalProps {
  handleClose?: any;
  existingData: VehicleEmissionType;
}

const VehicleEmissionModal = ({
  handleClose,
  existingData,
}: VehicleEmissionModalProps) => {
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const site = getLocalStorage(ls_vars.PROJECT_SITE_ID);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const { t, tCheck } = useTranslationCheck();
  const { call } = useApiCall();
  const [fuelTypeOptions, setFuelTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [vehicleEmission, setVehicleEmission] = useState<VehicleEmissionType>({
    id: 0,
    vehicleType: {
      id: 0,
      name: "",
      order: "",
      icon: "",
      enabled: true,
      renderName: "",
    },
    ref: "",
    milesPerGallonOrKmPerLiter: 0,
    emissionFactor: 0.0,
    fuelType: 0,
    // Add the missing required properties
    createdAt: "",
    modifiedAt: "",
    isActive: true,
    addedBy: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<
    { label: string; value: string; slug: any; vehicleType: VehicleType }[]
  >([]);
  const [validationErrors, setValidationErrors] = useState<{
    milesPerGallonOrKmPerLiter?: string;
    emissionFactor?: string;
  }>({});

  const handleChange = (e: { target: { name: string; value: any } }) => {
    const { name, value } = e.target;

    // Always update the state to allow normal text editing
    setVehicleEmission((prev: VehicleEmissionType) => ({
      ...prev,
      [name]: value,
    }));

    // Clear previous validation error for this field
    setValidationErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    // Validate numeric fields after updating state
    if (name === "milesPerGallonOrKmPerLiter" || name === "emissionFactor") {
      const numValue = parseFloat(value);
      if (value !== "" && !isNaN(numValue) && numValue <= 0) {
        setValidationErrors((prev) => ({
          ...prev,
          [name]: t("Translation.value_must_be_greater_than_zero"),
        }));
      }
    }
  };

  useEffect(() => {
    const loadInitData = async () => {
      setIsLoading(true);
      await getFuelTypes();
      await getVehiclesList();
      setIsLoading(false);
    };

    loadInitData();
    if (existingData && existingData.id) {
      setVehicleEmission(existingData);
    }
  }, [existingData]);

  const handleCreateVehicleEmissionSubmit = async (e: any) => {
    e.preventDefault();
    
    // Validate all numeric fields before submission
    const errors: typeof validationErrors = {};
    
    if (vehicleEmission.milesPerGallonOrKmPerLiter <= 0) {
      errors.milesPerGallonOrKmPerLiter = t("Translation.value_must_be_greater_than_zero");
    }
    
    if (vehicleEmission.emissionFactor <= 0) {
      errors.emissionFactor = t("Translation.value_must_be_greater_than_zero");
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return; // Stop submission if there are validation errors
    }
    
    const requestData = {
      id: vehicleEmission.id,
      vehicleType: typeof vehicleEmission.vehicleType === "object" ? vehicleEmission.vehicleType.id : vehicleEmission.vehicleType,
      milesPerGallonOrKmPerLiter: vehicleEmission.milesPerGallonOrKmPerLiter,
      emissionFactor: vehicleEmission.emissionFactor,
      fuelType: vehicleEmission.fuelType,
    };
    call(
      () => createVehicleEmission(requestData, region, project.ref, site),
      (data) => {
        setIsLoading(false);
        handleClose();
      },
      { successMessage: "Translation.vehicle_emission_add_success" }
    ),
      () => setIsLoading(false);
  };

  const handleUpdateVehicleEmissionSubmit = async (e: any) => {
    e.preventDefault();
    if (!existingData || existingData.id === undefined) {
      return;
    }

    // Validate all numeric fields before submission
    const errors: typeof validationErrors = {};
    
    if (vehicleEmission.milesPerGallonOrKmPerLiter <= 0) {
      errors.milesPerGallonOrKmPerLiter = t("Translation.value_must_be_greater_than_zero");
    }
    
    if (vehicleEmission.emissionFactor <= 0) {
      errors.emissionFactor = t("Translation.value_must_be_greater_than_zero");
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return; // Stop submission if there are validation errors
    }

    const id = existingData.id;
    call(
      () => updateVehicleEmission(id, vehicleEmission, region, project.ref),
      (data) => {
        setIsLoading(false);
        handleClose();
      },
      { successMessage: "Translation.vehicle_emission_edit_success" }
    ),
      () => setIsLoading(false);
  };

  const getFuelTypes = async () => {
    call(
      () => getFilteredCommonUtils("FUEL_TYPE", region),
      (data) => {
        const options = data?.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
         const sortedOptions = options?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setFuelTypeOptions(sortedOptions);
      }
    );
  };

  const getVehiclesList = () => {
    setIsLoading(true);
    call(
      () => getVehicleTypeList(project.ref),
      (data) => {
        setIsLoading(false);
        const parsedData: { label: string; value: string; slug: any; vehicleType: VehicleType }[] = data.results
          .filter((item: any) => item.enabled)
          .sort((a: any, b: any) => (a.order > b.order ? 1 : -1))
          .map((item: any) => ({
            label: tCheck(item.vehicleType.name),
            slug: item.vehicleType.name,
            value: item.vehicleType.id,
            vehicleType: item.vehicleType,
          }));
        const sortedOptions = parsedData?.sort((a: any, b: any) =>
          a.label.localeCompare(b.label)
        );
        setVehicleTypeOptions(sortedOptions);
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
  };

  return (
    <>
      {isLoading && <Loader />}
      <div
        className="modal fade  show vc-user-invite"
        id="invite_user_Modal"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-scrollable ">
          <div className="modal-content">
            <div className="modal-header">
              <div className="invite-user-header">
                <h2 className="text-left">
                  {t(
                    existingData && existingData.id
                      ? "Translation.vehicle_master_edit"
                      : "Translation.vehicle_master_add"
                  )}
                </h2>
              </div>
              <div className="invite-user-header vc-invite-from-procore-link">
                <Link onClick={handleClose} to="">
                  {t("Translation.close")}
                  <img src={images.modalCloseIcon} alt="" />
                </Link>
              </div>
            </div>
            <div className="modal-body">
              <div className="custom-form ">
                <form
                  onSubmit={
                    existingData && existingData.id
                      ? handleUpdateVehicleEmissionSubmit
                      : handleCreateVehicleEmissionSubmit
                  }
                >
                  <div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.vehicle_master_name")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <MaterialSelect
                          style={{
                            width: "22rem",
                            height: "",
                          }}
                          inputProps={{
                            className: "form-control",
                            id: "vehicleName",
                            name: "vehicleName",
                            required: true,
                          }}
                          value={(typeof vehicleEmission.vehicleType === "object" ? vehicleEmission.vehicleType?.id : vehicleEmission.vehicleType) || ""} // Add explicit value prop here,
                          onChange={(e: any) => {
                            const selectedOption = vehicleTypeOptions.find(
                              (obj) => obj.value === e.target.value
                            );
                            const currentvehicleType: VehicleType =
                              selectedOption?.vehicleType ??
                              ({} as VehicleType);
                            setVehicleEmission((prev: VehicleEmissionType) => ({
                              ...prev,
                              vehicleType: currentvehicleType,
                            }));
                          }}
                        >
                          {vehicleTypeOptions.map((item) => (
                            <MenuItem value={item.value} key={item.value}>
                              {tCheck(item.label)}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.fuel_type")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <MaterialSelect
                          style={{
                            width: "22rem",
                          }}
                          inputProps={{
                            className: "form-control",
                            id: "fuelType",
                            name: "fuelType",
                            required: true,
                          }}
                          value={vehicleEmission.fuelType || ""} // Add explicit value prop here,
                          onChange={(e: any) => handleChange(e)}
                        >
                          {fuelTypeOptions.map((item) => (
                            <MenuItem value={item.value} key={item.value}>
                              {tCheck(item.label)}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.milesgallons_or_kmLitres")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <input
                          type="number"
                          className={`form-control ${validationErrors.milesPerGallonOrKmPerLiter ? "is-invalid" : ""}`}
                          id="milesPerGallonOrKmPerLiter"
                          name="milesPerGallonOrKmPerLiter"
                          value={vehicleEmission.milesPerGallonOrKmPerLiter}
                          onChange={handleChange}
                          required
                        />
                        {validationErrors.milesPerGallonOrKmPerLiter && (
                          <div className="invalid-feedback" style={{ display: "block" }}>
                            {validationErrors.milesPerGallonOrKmPerLiter}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.emission_factor")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <input
                          type="number"
                          className={`form-control ${validationErrors.emissionFactor ? "is-invalid" : ""}`}
                          id="emissionFactor"
                          name="emissionFactor"
                          value={vehicleEmission.emissionFactor}
                          onChange={handleChange}
                          required
                        />
                        {validationErrors.emissionFactor && (
                          <div className="invalid-feedback" style={{ display: "block" }}>
                            {validationErrors.emissionFactor}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="submit" className="btn theme-btn">
                        {t("Translation.submit")}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VehicleEmissionModal;
